import { FormLanguage, Language } from '../enums/languages';

/**
 * A language offered by the in-form language switcher. `locale` is a
 * {@link FormLanguage} so the switcher's value type lines up with the
 * translation form fields it drives.
 */
type FormLanguageOption = {
  locale: FormLanguage;
  name: Record<string, string>;
};

export const languagesWithoutNL: FormLanguageOption[] = [
  {
    locale: Language.EN,
    name: {
      de: 'English',
      ru: 'English',
      pt: 'English',
      ja: 'English',
      en: 'English',
      cn: 'English',
      it: 'English',
      fr: 'English',
      es: 'English',
      nl: 'English',
    },
  },
  {
    locale: Language.FR,
    name: {
      de: 'Français',
      ru: 'Français',
      pt: 'French',
      ja: 'Français',
      en: 'Français',
      cn: 'Français',
      it: 'Français',
      fr: 'Français',
      es: 'Français',
      nl: 'Français',
    },
  },
  {
    locale: Language.DE,
    name: {
      de: 'Deutsch',
      ru: 'Deutsch',
      ja: 'Deutsch',
      en: 'Deutsch',
      cn: 'Deutsch',
      it: 'Deutsch',
      fr: 'Deutsch',
      es: 'Deutsch',
      nl: 'Deutsch',
    },
  },
  {
    locale: Language.IT,
    name: {
      de: 'Italiano',
      ru: 'Italiano',
      ja: 'Italiano',
      en: 'Italiano',
      cn: 'Italiano',
      it: 'Italiano',
      fr: 'Italiano',
      es: 'Italiano',
      nl: 'Italiano',
    },
  },
  {
    locale: Language.ES,
    name: {
      de: 'Español',
      ru: 'Español',
      pt: 'Spanish',
      ja: 'Español',
      en: 'Español',
      cn: 'Español',
      it: 'Español',
      fr: 'Español',
      es: 'Español',
      nl: 'Español',
    },
  },
  {
    locale: Language.JA,
    name: {
      de: '日本語',
      ru: '日本語',
      ja: '日本語',
      en: '日本語',
      cn: '日本語',
      it: '日本語',
      fr: '日本語',
      es: '日本語',
      nl: '日本語',
    },
  },
  {
    locale: Language.CN,
    name: {
      de: '中文',
      ru: '中文',
      ja: '中文',
      en: '中文',
      cn: '中文',
      it: '中文',
      fr: '中文',
      es: '中文',
      nl: '中文',
    },
  },
  {
    locale: Language.RU,
    name: {
      de: 'Russian',
      ru: 'Russian',
      ja: 'Russian',
      en: 'Russian',
      cn: 'Russian',
      it: 'Russian',
      fr: 'Russian',
      es: 'Russian',
      nl: 'Russian',
    },
  },
];

export const languages: FormLanguageOption[] = [
  ...languagesWithoutNL.slice(0, 2),
  {
    locale: Language.NL,
    name: {
      de: 'Nederlands',
      ru: 'Nederlands',
      pt: 'Dutch',
      ja: 'Nederlands',
      en: 'Nederlands',
      cn: 'Nederlands',
      it: 'Nederlands',
      fr: 'Nederlands',
      nl: 'Nederlands',
      es: 'Nederlands',
    },
  },
  ...languagesWithoutNL.slice(3),
];

export const userLanguages: { locale: Language; name: Record<string, string> }[] = [
  ...languagesWithoutNL.slice(0, 2),
  {
    locale: Language.NL_BE,
    name: {
      de: 'Nederlands (BE)',
      ru: 'Nederlands (BE)',
      pt: 'Dutch (BE)',
      ja: 'Nederlands (BE)',
      en: 'Nederlands (BE)',
      cn: 'Nederlands (BE)',
      it: 'Nederlands (BE)',
      fr: 'Nederlands (BE)',
      nl: 'Nederlands (BE)',
      es: 'Nederlands (BE)',
    },
  },
  {
    locale: Language.NL_NL,
    name: {
      de: 'Nederlands (NL)',
      ru: 'Nederlands (NL)',
      pt: 'Dutch (NL)',
      ja: 'Nederlands (NL)',
      en: 'Nederlands (NL)',
      cn: 'Nederlands (NL)',
      it: 'Nederlands (NL)',
      fr: 'Nederlands (NL)',
      nl: 'Nederlands (NL)',
      es: 'Nederlands (NL)',
    },
  },
  ...languagesWithoutNL.slice(3),
];

export const languagesOptions: { label: string; value: FormLanguage }[] =
  languages.map((language) => ({
    label: language.locale.toUpperCase(),
    value: language.locale,
  }));

// German only on the steps holding the custom mail messages (#2498); other forms still skip it
export const mailMessageLanguagesOptions: {
  label: string;
  value: FormLanguage;
}[] = [
  ...languagesOptions.slice(0, 3),
  { label: Language.DE.toUpperCase(), value: Language.DE },
  ...languagesOptions.slice(3),
];

export const userLanguagesOptions = userLanguages.map((language) => ({
  label: language.name['en'],
  value: language.locale,
}));
